import React, { useEffect, useMemo, useState } from "react";
import LineChart, { LineChartDatum } from "../LineChart/LineChart";
import ContentArea from "../ContentArea/ContentArea";
import StatusMessage from "../StatusMessage/StatusMessage";
import Layout from "./Layout/Layout";
import MempoolSpaceAdapter from "../../utils/Adapters/MempoolSpaceAdapter";
import "./HashratePage.css";
import { useSeo } from "../../utils/Hooks/useSeo";

type HashrateHistoryPoint = {
  timestamp: number;
  avgHashrate: number;
};

const EXA = 10 ** 18;
const DAY_SECONDS = 24 * 60 * 60;
const SEO_DESCRIPTION =
  "Monitor Bitcoin hashrate with weekly history and performance change windows from 7 days to 5 years.";
const CHANGE_PERIODS = [
  { label: "Week Change", days: 7 },
  { label: "Month Change", days: 30 },
  { label: "6 Month Change", days: 182 },
  { label: "Year Change", days: 365 },
  { label: "3 Years Change", days: 365 * 3 },
  { label: "5 Years Change", days: 365 * 5 },
] as const;

const formatDate = (date: Date) =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);

const formatHashrate = (hashrate: number) =>
  `${new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(hashrate / EXA)} EH/s`;

const formatHashrateValue = (hashrate: number) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(hashrate / EXA);

const getLastPointBefore = (
  points: HashrateHistoryPoint[],
  targetTimestampSeconds: number,
) => {
  for (let index = points.length - 1; index >= 0; index -= 1) {
    if (points[index].timestamp <= targetTimestampSeconds) {
      return points[index];
    }
  }

  return undefined;
};

const formatChange = (changePercent: number) =>
  `${changePercent >= 0 ? "+" : ""}${changePercent.toFixed(2)} %`;

const getChangeTone = (changePercent: number) => {
  if (Math.abs(changePercent) < 0.005) {
    return "neutral";
  }

  return changePercent > 0 ? "positive" : "negative";
};

export const HashratePage = () => {
  const [weeklyHashrate, setWeeklyHashrate] = useState<HashrateHistoryPoint[] | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "Bitcoin Hashrate",
    description: SEO_DESCRIPTION,
    canonicalPath: "/hashrate",
  });

  useEffect(() => {
    const load = async () => {
      const response =
        (await MempoolSpaceAdapter.getBitcoinMiningHashrate10yWeeklyAverages()) as HashrateHistoryPoint[];

      setWeeklyHashrate(Array.isArray(response) ? response : []);
      setLoading(false);
    };

    load();
  }, []);

  const chartData = useMemo<LineChartDatum[]>(
    () =>
      (weeklyHashrate ?? []).map((point) => ({
        date: new Date(point.timestamp * 1000),
        value: point.avgHashrate,
      })),
    [weeklyHashrate],
  );

  const changeStats = useMemo(
    () =>
      (weeklyHashrate ?? []).length
        ? CHANGE_PERIODS.map((period) => {
            const latestPoint = weeklyHashrate![weeklyHashrate!.length - 1];
            const historicalPoint = getLastPointBefore(
              weeklyHashrate!,
              latestPoint.timestamp - period.days * DAY_SECONDS,
            );

            if (!historicalPoint || historicalPoint.avgHashrate <= 0) {
              return {
                label: period.label,
                value: "-",
                tone: "neutral",
              };
            }

            const changePercent =
              ((latestPoint.avgHashrate - historicalPoint.avgHashrate) /
                historicalPoint.avgHashrate) *
              100;

            return {
              label: period.label,
              value: formatChange(changePercent),
              tone: getChangeTone(changePercent),
            };
          })
        : [],
    [weeklyHashrate],
  );

  const content = () => {
    if (loading) {
      return (
        <div className="progress-circle">
          <progress className="pure-material-progress-circular" />
        </div>
      );
    }

    if (!chartData.length) {
      return (
        <StatusMessage
          title="We could not retrieve hashrate history."
          description="Please try again in a minute."
          minHeightRem={24}
        />
      );
    }

    const latestPoint = weeklyHashrate![weeklyHashrate!.length - 1];
    return (
      <div className="content-lines">
        <section className="hashrate-overview-card">
          <div className="hashrate-overview-top">
            <div>
              <h1 className="hashrate-overview-title">Bitcoin Hashrate</h1>
              <div className="hashrate-overview-subtitle">{SEO_DESCRIPTION}</div>
            </div>
            <div className="hashrate-current-panel">
              <div className="hashrate-current-label">Current</div>
              <div className="hashrate-current-value">
                {formatHashrate(latestPoint.avgHashrate)}
              </div>
            </div>
          </div>

          <div className="hashrate-stats-grid">
            {changeStats.map((stat) => (
              <div className="hashrate-stat-card" key={stat.label}>
                <div className="hashrate-stat-label">{stat.label}</div>
                <div className={`hashrate-stat-value ${stat.tone}`}>
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="hashrate-chart-wrap">
          <LineChart
            data={chartData}
            widthRem={60}
            heightRem={24}
            title="Bitcoin Hashrate (Weekly Average)"
            xAxisLabel="Date"
            yAxisLabel="Hashrate (EH/s)"
            valueFormatter={formatHashrate}
            yAxisValueFormatter={formatHashrateValue}
            dateFormatter={formatDate}
          />
        </div>

        <div className="powered-by">
          Powered by{" "}
          <a
            className="hashrate-link"
            href="https://mempool.space/"
            target="_blank"
            rel="noreferrer"
          >
            mempool.space
          </a>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <ContentArea isTopArea>
        {content()}
      </ContentArea>
    </Layout>
  );
};
